'use client';

import { ApiKeys, fetcher } from '@insta-club/api';
import { useCustomForm, useOtpCountdown } from '@insta-club/hooks';
import { useSessionStore } from '@insta-club/state';
import { Button, Form, Input, Modal } from '@insta-club/ui';
import { AccountIdentityFormValues, AccountIdentitySchema } from '@insta-club/validator';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';
import OTPInput from 'react-otp-input';
import { PenIcon } from 'src/libs/components/icons';

const content = {
    email: {
        title: 'ویرایش ایمیل',
        description: 'توجه داشته باشید ویرایش ایمیل حساب کاربری شما حداکثر ۳ بار قابل انجام است',
        sent_description: 'کد تایید برای ویرایش تلفن همراه برای شما ارسال شد'
    },
    mobile: {
        title: 'ویرایش تلفن همراه',
        description: 'توجه داشته باشید ویرایش تلفن همراه حساب کاربری شما حداکثر ۳ بار قابل انجام است',
        sent_description: 'کد تایید برای ویرایش ایمیل برای شما ارسال شد'
    }
};

const ModifyUserIdentity = ({
    open,
    onClose,
    type
}: {
    open: boolean;
    onClose: () => void;
    type: 'email' | 'mobile' | null;
}) => {
    const updateSession = useSessionStore((state) => state.updateSession);
    const { start, isDisabled, formatted } = useOtpCountdown({
        duration: 180,
        storageKey: 'otp-end'
    });
    const [step, setStep] = useState<'input' | 'verify'>('input');

    const onSubmit: SubmitHandler<AccountIdentityFormValues> = async (data) => {
        try {
            const payload = type === 'email' ? { email: data.email } : { mobile: data.mobile };
            if (step === 'input') {
                await fetcher(ApiKeys.SEND_VERIFICATION_CODE, 'dashboard', 'POST', {
                    body: {
                        username: type ? payload[type] : ''
                    }
                });
                setStep('verify');
                start();
                toast.success(`کد تایید با موفقیت به ${type === 'email' ? 'ایمیل' : 'تلفن همراه'} شما ارسال شد.`);
            } else if (step === 'verify') {
                await fetcher(ApiKeys.PATCH_USER_IDENTITY, 'dashboard', 'PATCH', {
                    body: {
                        username: type ? payload[type] : '',
                        verification_code: data.verification_code
                    }
                });
                onClose();
                setStep('input');
                form.reset();
                updateSession();
            }
        } catch (err: unknown) {
            if (err instanceof Error) {
                toast.error('خطای ناشناخته رخ داده است');
            } else {
                const error = err as { message: string };
                toast.error(error.message);
            }
        }
    };

    const form = useCustomForm(AccountIdentitySchema);
    const { reset } = form;

    useEffect(() => {
        if (!open) {
            setStep('input');
            reset();
        }
    }, [open, reset]);

    const handleResendOTP = async () => {
        await fetcher(ApiKeys.SEND_VERIFICATION_CODE, 'web', 'POST', {
            body: {
                username: type ? form.getValues(type) : 'mobile'
            }
        });
        start();
    };

    return (
        <>
            <Modal
                title={type ? content[type].title : ''}
                description={type ? content[type][step === 'input' ? 'description' : 'sent_description'] : ''}
                isOpen={open}
                onClose={onClose}
                closeOnOverlayClick={false}
            >
                {step === 'verify' && (
                    <>
                        <p className='mt-6 mb-2 text-center text-sm font-normal text-gray-500'>
                            یک کد تایید به {type === 'mobile' ? 'تلفن همراه' : 'ایمیل'} شما ارسال شده است
                        </p>
                        <div className='mb-6 flex items-center justify-center'>
                            <span className='ml-2 text-base font-semibold text-gray-600'>
                                {form.getValues(type ?? 'mobile')}
                            </span>
                            <button
                                onClick={() => {
                                    setStep('input');
                                }}
                                className='text-primary-600 flex cursor-pointer items-center text-sm font-normal'
                            >
                                <PenIcon className='ml-1' />
                                ویرایش
                            </button>
                        </div>
                    </>
                )}

                <Form form={form} onSubmit={onSubmit}>
                    <div className='space-y-5'>
                        {type === 'email' && step === 'input' && <Input name='email' label='ایمیل جدید' />}
                        {type === 'mobile' && step === 'input' && (
                            <Input name='mobile' label='تلفن همراه جدید' max={11} maxLength={11} />
                        )}

                        {step === 'verify' && (
                            <>
                                <OTPInput
                                    {...form.register('verification_code')}
                                    onChange={(val) => form.setValue('verification_code', val)}
                                    value={form.watch('verification_code')}
                                    numInputs={4}
                                    shouldAutoFocus
                                    inputStyle={{
                                        direction: 'ltr',
                                        minWidth: '3rem',
                                        height: '3rem',
                                        fontSize: '1.5rem',
                                        borderRadius: '8px',
                                        border: '1px solid #E5E7EB',
                                        textAlign: 'center',
                                        backgroundColor: '#F9FAFB',
                                        borderColor:
                                            form.formState.errors && form.formState.errors['verification_code']
                                                ? 'red'
                                                : ''
                                    }}
                                    containerStyle={{
                                        direction: 'ltr',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: '1rem',
                                        marginBottom: '10px'
                                    }}
                                    renderInput={(props) => <input dir='ltr' {...props} inputMode='numeric' />}
                                />
                                {form.formState.errors && form.formState.errors['verification_code'] && (
                                    <p className='mb-3 text-center text-sm text-red-500'>
                                        {form.formState.errors['verification_code']?.message as unknown as string}
                                    </p>
                                )}
                                {isDisabled ? (
                                    <p className='mb-6 text-center text-sm font-normal text-gray-500'>
                                        ارسال مجدد ( {formatted} )
                                    </p>
                                ) : (
                                    <p
                                        onClick={() => handleResendOTP()}
                                        className='text-primary-500 mb-6 cursor-pointer text-center text-base font-semibold'
                                    >
                                        ارسال مجدد
                                    </p>
                                )}
                            </>
                        )}

                        <Button loading={form.formState.isSubmitting} fullWidth type='submit'>
                            {step === 'verify' ? 'ثبت کد تایید' : 'دریافت کد تایید'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
};

export default ModifyUserIdentity;

